import { WrapperEntitySelector } from '@components/Inputs';
import { ICreateGroupRule, IForm, IRule, TMode } from '@ts/ui-models';
import { generateSearchEntitySelector } from '@modules/entitySelector/entitySelector.utils';
import { Grid, TextInput, Typography, GridContainerFullHeight, findRuleById, Autocomplete, Switch, Loading } from '@ts/storybook';
import { CGroupTemplate, ERuleType } from '@ts/ui-models';
import { Suspense, useCallback, useEffect, useRef } from 'react';
import { CreateGroupRuleConstraintDto } from '@ts/ui-models';
import React from 'react';

const FlowForm = React.lazy(() => import('@ts/storybook/modules/FlowForm/FlowForm'));

interface IGroupTemplateFormProps extends IForm {
  values: CGroupTemplate;
}

const GroupTemplateForm = (props: IGroupTemplateFormProps) => {
  const { onChange, values, mode, onCallbackChange } = props;
  const { name, description, ordered } = values;
  const isViewMode = mode === 'view';

  const entityRef = useRef(values);

  const optionsRules = [
    { label: 'Items inside', value: ERuleType.ITEMS_INSIDE },
    { label: 'Limit aggregated status indicator value', value: ERuleType.LIMIT_AGGREGATED_STATUS_INDICATOR_VALUE },
    { label: 'Require status indicator value', value: ERuleType.REQUIRE_STATUS_INDICATOR_VALUE },
  ];

  const sanitizeConstraintByRuleType = (
    type: ERuleType,
    constraint: Partial<CreateGroupRuleConstraintDto> = {}
  ): Partial<CreateGroupRuleConstraintDto> => {
    const c = constraint as any;
    switch (type) {
      case ERuleType.ITEMS_INSIDE:
        return {
          itemTypeIds: c.itemTypeIds,
          minCount: c.minCount,
          maxCount: c.maxCount,
        };

      case ERuleType.LIMIT_AGGREGATED_STATUS_INDICATOR_VALUE:
        return {
          indicatorTypeId: c.indicatorTypeId,
          minValue: c.minValue,
          maxValue: c.maxValue,
        };

      case ERuleType.REQUIRE_STATUS_INDICATOR_VALUE:
        return {
          indicatorTypeId: c.indicatorTypeId,
          requiredValue: c.requiredValue,
        };

      default:
        return {};
    }
  };

  const updateRuleById = (
    rules: ICreateGroupRule[] | IRule[],
    cid: string,
    update: Partial<ICreateGroupRule>
  ): ICreateGroupRule[] | IRule[] => {
    return rules.map(rule => {
      const isMatch = (rule as ICreateGroupRule).cid === cid;

      if (isMatch) {
        const groupRuleType = rule.groupRuleTypeId as ERuleType;
        const cleanedConstraints =
          'constraints' in update ? sanitizeConstraintByRuleType(groupRuleType, update.constraints) : rule.constraints;
        return {
          ...rule,
          ...update,
          constraints: cleanedConstraints,
        };
      }

      if ('rules' in rule && Array.isArray(rule.rules)) {
        return {
          ...rule,
          rules: updateRuleById(rule.rules, cid, update),
        };
      }

      return rule;
    });
  };
  const onChangeRule = useCallback(
    <K extends keyof ICreateGroupRule>(
      idRule: string,
      path: K,
      value: ICreateGroupRule[K] | ((prev: ICreateGroupRule[K]) => ICreateGroupRule[K])
    ) => {
      const currentRules = entityRef.current.rules;
      const currentRule: ICreateGroupRule | null = findRuleById(currentRules, idRule) as ICreateGroupRule | null;
      const previousValue = currentRule?.[path];

      const nextValue =
        typeof value === 'function' ? (value as (prev: ICreateGroupRule[K]) => ICreateGroupRule[K])(previousValue!) : value;

      const updatedRules = updateRuleById(currentRules, idRule, { [path]: nextValue });
      onChange('rules', updatedRules);
    },
    [onChange]
  );

  const renderGroupForm = useCallback(
    (props: { idParent?: string | null; entity: ICreateGroupRule | ICreateGroupRule }) => {
      const { cid, name, order, groupRuleTypeId, groupRuleType, constraints } = props.entity as any;
      const grtId = groupRuleTypeId ?? groupRuleType?.id;
      if (!cid) return null;
      return (
        <>
          <Grid size={12}>
            <Typography variant="body1" color="primary" className="font-semibold" text={'Name'} />
            <TextInput
              value={name}
              onChangeValue={v => onChangeRule(cid, 'name', v.toString())}
              placeholder="Nombre"
              isReadOnly={isViewMode}
            />
          </Grid>
          {ordered && (
            <Grid size={12}>
              <Typography variant="body1" color="primary" className="font-semibold" text={'Order'} />
              <TextInput
                value={order}
                onChangeValue={v => onChangeRule(cid, 'order', Number(v))}
                placeholder="0"
                type="number"
                isReadOnly={isViewMode}
              />
            </Grid>
          )}
          <Grid size={12}>
            <Typography variant="body1" color="primary" className="font-semibold" text={'Type'} />
            <Autocomplete
              multiple={false}
              value={optionsRules.find(rule => rule.value === grtId)}
              onChangeValue={v => {
                onChangeRule(cid, 'groupRuleTypeId', v?.value ?? undefined);
                setTimeout(() => {
                  onChangeRule(cid, 'constraints', undefined);
                }, 200);
              }}
              options={optionsRules}
              stProps={{ textInput: { isReadOnly: isViewMode } }}
            />
          </Grid>

          {grtId === ERuleType.ITEMS_INSIDE && (
            <Grid size={12}>
              <Typography variant="body1" color="primary" className="font-semibold" text={'Item types'} />
              <WrapperEntitySelector
                searchComponent={generateSearchEntitySelector('itemType')}
                value={constraints?.itemTypeIds}
                multiple
                onChange={v => {
                  onChangeRule(cid, 'constraints', {
                    ...constraints,
                    itemTypeIds: (v as string[]) ?? undefined,
                    indicatorTypeId: undefined,
                  });
                }}
                isReadOnly={isViewMode}
              />
            </Grid>
          )}

          {(grtId === ERuleType.LIMIT_AGGREGATED_STATUS_INDICATOR_VALUE ||
            grtId === ERuleType.REQUIRE_STATUS_INDICATOR_VALUE) && (
            <Grid size={12}>
              <Typography variant="body1" color="primary" className="font-semibold" text={'Status indicators'} />
              <WrapperEntitySelector
                searchComponent={generateSearchEntitySelector('indicatorType')}
                value={constraints?.indicatorTypeId}
                onChange={v => {
                  onChangeRule(cid, 'constraints', {
                    ...constraints,
                    indicatorTypeId: (v as string) ?? undefined,
                    itemTypeIds: undefined,
                  });
                }}
                withoutId
                isReadOnly={isViewMode}
              />
            </Grid>
          )}

          {(grtId === ERuleType.ITEMS_INSIDE || grtId === ERuleType.LIMIT_AGGREGATED_STATUS_INDICATOR_VALUE) &&
            (constraints?.indicatorTypeId || constraints?.itemTypeIds) && (
              <>
                <Grid size={12}>
                  <Typography
                    variant="body1"
                    color="primary"
                    className="font-semibold"
                    text={constraints?.indicatorTypeId ? 'Value' : 'Count'}
                  />
                </Grid>
                <Grid container spacing={1}>
                  <Grid size={6}>
                    <TextInput
                      label="Min"
                      value={constraints?.minCount ?? constraints?.minValue ?? null}
                      placeholder="0"
                      onChangeValue={v =>
                        onChangeRule(cid, 'constraints', prev => {
                          const latestConstraints = prev ?? {};
                          return {
                            ...latestConstraints,
                            minCount: Number(v),
                            minValue: Number(v),
                          };
                        })
                      }
                      isReadOnly={isViewMode}
                      type="number"
                    />
                  </Grid>
                  <Grid size={6}>
                    <TextInput
                      label="Max"
                      value={constraints?.maxCount ?? constraints?.maxValue ?? null}
                      placeholder="0"
                      onChangeValue={v => {
                        onChangeRule(cid, 'constraints', prev => {
                          const latestConstraints = prev ?? {};
                          return {
                            ...latestConstraints,
                            maxCount: Number(v),
                            maxValue: Number(v),
                          };
                        });
                      }}
                      isReadOnly={isViewMode}
                      type="number"
                    />
                  </Grid>
                </Grid>
              </>
            )}

          {grtId === ERuleType.REQUIRE_STATUS_INDICATOR_VALUE && constraints?.indicatorTypeId && (
            <Grid size={12}>
              <Grid size={12}>
                <Typography variant="body1" color="primary" className="font-semibold" text={'Value'} />
              </Grid>
              <TextInput
                value={constraints?.requiredValue?.toString() ?? null}
                onChangeValue={v => onChangeRule(cid, 'constraints', { ...constraints, requiredValue: v })}
                isReadOnly={isViewMode}
              />
            </Grid>
          )}
        </>
      );
    },
    [onChange, isViewMode, ordered]
  );

  useEffect(() => {
    entityRef.current = values;
  }, [values]);

  return (
    <Suspense fallback={<Loading />}>
      <GridContainerFullHeight spacing={2}>
        <TextInput
          label="Name"
          value={name}
          onChangeValue={onCallbackChange('name')}
          placeholder="Nombre"
          isReadOnly={isViewMode}
        />
        <TextInput
          label="Description"
          value={description}
          onChangeValue={onCallbackChange('description')}
          placeholder="Nombre"
          isReadOnly={isViewMode}
        />
        <Switch rightLabel="Ordered" checked={ordered} onChangeValue={onCallbackChange('ordered')} isReadOnly={isViewMode} />
        <Typography variant="h6" text={'Rules'} className="mt-2!important" />
        <Grid size={'grow'}>
          <FlowForm
            entity={values}
            field="rules"
            form={renderGroupForm}
            onChangeEntity={v => onChange('rules', v.rules)}
            isViewMode={isViewMode}
          />
        </Grid>
      </GridContainerFullHeight>
    </Suspense>
  );
};

export default GroupTemplateForm;
